/**
 * Plain-language labels & helpers for dealer-facing UI.
 * Keep jargon out of headings; put definitions in tip cards.
 */

import { money } from "./api.js";

/** Friendly job names for contractors */
export const JOB_LABELS = {
  enclose_covered_patio: "Enclose a covered patio",
  open_yard_quick_shade: "Quick shade for open yard",
  outdoor_room_premium: "Premium outdoor room",
  poolside_comfort: "Pool / patio comfort",
  wind_privacy: "Wind, privacy & UV screens",
};

/** Good / better / best → how to open the conversation */
export const LADDER_LABELS = {
  good: { short: "Good", full: "Start here (value)", tip: "Competitive entry option — win on price & speed" },
  better: { short: "Better", full: "Lead with this", tip: "Best balance of quality and ticket size for most homes" },
  best: { short: "Best", full: "Premium upsell", tip: "Highest ticket when the home and buyer can support it" },
};

export const TIER_LABELS = {
  hot: { short: "Hot", tip: "Call first this week — strongest fit" },
  standard: { short: "Solid", tip: "Worth a visit or measure appointment" },
  watch: { short: "Watch", tip: "Lower priority — verify on Street View before outreach" },
};

export function jobLabel(id) {
  if (!id) return "—";
  return JOB_LABELS[id] || String(id).replace(/_/g, " ");
}

export function ladderLabel(focus) {
  const k = String(focus || "").toLowerCase();
  return LADDER_LABELS[k] || { short: focus || "—", full: focus || "—", tip: "" };
}

export function tierLabel(tier) {
  const k = String(tier || "").toLowerCase();
  return TIER_LABELS[k] || { short: tier || "—", tip: "" };
}

export function pct(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return `${Math.round(Number(n) * 100)}%`;
}

export function scoreLabel(score) {
  const s = Number(score || 0);
  if (s >= 0.75) return "Strong fit";
  if (s >= 0.55) return "Good fit";
  if (s >= 0.4) return "Possible fit";
  return "Verify first";
}

export function ticketLabel(band) {
  const map = {
    entry: "Entry ticket",
    core: "Core ticket",
    premium: "Premium ticket",
    estate: "Estate ticket",
  };
  return map[band] || band || "";
}

/** Help tip box — always plain English */
export function tip(title, body) {
  return `
    <aside class="help-tip" role="note">
      <div class="help-tip-title">${title}</div>
      <div class="help-tip-body">${body}</div>
    </aside>`;
}

export function explainMoney() {
  return tip(
    "How to read the money numbers",
    `<ul class="help-list">
      <li><strong>Package range</strong> — what the suggested product mix might sell for installed (low–high). <em>Not a quote.</em></li>
      <li><strong>Typical ticket</strong> — middle of that range for an average lead in this pack.</li>
      <li><strong>Chance of close</strong> — rough planning % that a lead becomes a sold job (score, home value, job type).</li>
      <li><strong>Likely revenue</strong> — package middle × chance of close, summed across leads. Use for territory planning only.</li>
      <li><strong>Safer / stretch</strong> — likely revenue × 0.7, or a more optimistic case. Not a forecast.</li>
    </ul>`
  );
}

export function explainLeads() {
  return tip(
    "How to use this lead list",
    `<ol class="help-list">
      <li><strong>Start with Hot</strong> leads — highest priority this week.</li>
      <li>Read <strong>What to sell</strong> — brand + Good/Better/Best starting point.</li>
      <li>Check Street View in <a href="#/review">Review</a> before you visit.</li>
      <li><strong>Phone/email only</strong> when the homeowner opted in (Capture) or you have legal consent. Pack addresses are for contractor planning — not cold spam.</li>
    </ol>`
  );
}

export function kpi(value, label, hint) {
  return `
    <div class="kpi" title="${hint || ""}">
      <b>${value}</b>
      <span>${label}</span>
      ${hint ? `<em class="kpi-hint">${hint}</em>` : ""}
    </div>`;
}

export function actionCards(items) {
  return `
    <div class="action-grid">
      ${items
        .map(
          (it) => `
        <a class="action-card" href="${it.href}">
          <div class="action-card-kicker">${it.kicker || "Next"}</div>
          <div class="action-card-title">${it.title}</div>
          <div class="action-card-desc">${it.desc || ""}</div>
        </a>`
        )
        .join("")}
    </div>`;
}

export function brandFriendly(id, name) {
  return name || id || "—";
}

export function formatWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return String(iso);
  }
}

export function ticketMixHtml(buckets) {
  const t = buckets || {};
  const rows = [
    ["Under $12k", t.under_12k, "Awning / small screen jobs"],
    ["$12k – $35k", t["12k_35k"], "Most screen enclosures"],
    ["$35k – $60k", t["35k_60k"], "Larger multi-elevation jobs"],
    ["$60k+", t["60k_plus"], "Outdoor rooms / structures"],
  ];
  const total = rows.reduce((s, [, n]) => s + (Number(n) || 0), 0) || 1;
  return `
    <div class="ticket-bars">
      ${rows
        .map(([label, n, tipText]) => {
          const v = Number(n) || 0;
          const pctW = Math.max(4, Math.round((v / total) * 100));
          return `
          <div class="ticket-row" title="${tipText}">
            <div class="ticket-label">${label}</div>
            <div class="ticket-track"><div class="ticket-fill" style="width:${pctW}%"></div></div>
            <div class="ticket-n"><strong>${v}</strong> homes</div>
          </div>`;
        })
        .join("")}
    </div>`;
}

export function moneyRange(lo, hi) {
  return `${money(lo)} – ${money(hi)}`;
}
